import { Form, message, Spin } from 'antd';
import RadioCards from 'components/RadioCards/RadioCards';
import { Banner } from 'components/ui/Banner';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { commonService, familyService } from 'services';
import { FAMILY_APPLICATION_STEPS, FAMILY_BRANCH_MAP, FAMILY_COMPONENT_MAP } from '../../familyApplicationConstants';
import { useAsync } from 'react-async-hook';
import PageMeta from 'components/PageMeta/PageMeta';
import { FAMILY_STATUS_MAP } from '../../familyApplicationConstants';
import { getNextStep, getPreviousStep, stepConfigAtom } from '../../atoms';
import { useAtomValue } from 'jotai';
import { getApplicantFirstName } from '../../utils';
import { applyMilitaryFieldCleaning } from '../../utils/militaryDataCleaning';
import { ApplicationWizardActions } from 'components/application-wizards/ApplicationWizardActions';
import { OrderTitleQuestions } from './FamilyApplicationStatus/OrderTitleQuestions';

const NON_DEPLOYED_OPTION_ID = FAMILY_STATUS_MAP.nonDeployed.id;
const WEEKEND_DRILL_OPTION_ID = FAMILY_STATUS_MAP.weekendDrill.id;
const CIVILIAN_TECHNICIAN_OPTION_ID = FAMILY_STATUS_MAP.dualStatusTechnicianCurrentlyCivilian.id;

export function FamilyApplicationStatus({
  application,
  setApplication,
  household,
  loading,
  setStep,
  readOnly = false,
}) {
  const stepConfig = useAtomValue(stepConfigAtom);

  const [submitting, setSubmitting] = useState(false);

  const isCompletedBySponsor = application?.completedBySponsor === true;

  // Form state
  const [form] = Form.useForm();
  const selectedStatusFormValue = Form.useWatch('selectedStatus', form);

  // API fetches
  const { result: statusOptions = null } = useAsync(async () => {
    const _branchId = application?.applicant?.militaryDetails?.branch?.id;
    const _militaryComponentId = application?.applicant?.militaryDetails?.militaryComponent?.id;
    if (_branchId === undefined || _militaryComponentId === undefined) {
      return null;
    }

    const _statuses = await commonService.get(
      `/families/ref-data/military-info/statuses/lookup?programSponsorId=${_branchId}&componentId=${_militaryComponentId}`,
      {
        cache: { interpretHeader: false },
      },
    );

    // put non-deployed option at the top
    const _nonDeployedOption = _statuses.find((_status) => _status.id === NON_DEPLOYED_OPTION_ID);
    return [
      ...(_nonDeployedOption ? [_nonDeployedOption] : []),
      ..._statuses.filter((_status) => _status.id !== NON_DEPLOYED_OPTION_ID && _status.id !== WEEKEND_DRILL_OPTION_ID),
    ];
  }, [application]);

  const { result: orderTitles = null, loading: orderTitlesLoading } = useAsync(async () => {
    if (
      typeof application?.applicant?.militaryDetails?.militaryComponent?.id !== 'number' ||
      typeof selectedStatusFormValue !== 'number'
    ) {
      return null;
    }

    return commonService.get(
      `/families/ref-data/military-info/order-titles?componentId=${application?.applicant?.militaryDetails?.militaryComponent?.id}&statusId=${selectedStatusFormValue}`,
      {
        cache: { interpretHeader: false },
      },
    );
  }, [application, selectedStatusFormValue]);

  const showOrderTitleQuestions = useMemo(() => {
    // Branch of Service - check
    if (
      ![FAMILY_BRANCH_MAP.army.id, FAMILY_BRANCH_MAP.airForce.id].includes(
        application?.applicant?.militaryDetails?.branch?.id,
      )
    ) {
      return false;
    }

    // Component - check (National Guard or Reserve)
    if (
      ![FAMILY_COMPONENT_MAP.nationalGuard.id, FAMILY_COMPONENT_MAP.reserve.id].includes(
        application?.applicant?.militaryDetails?.militaryComponent?.id,
      )
    ) {
      return false;
    }

    // Status - check (only "On military orders (30+ days)" or "Dual status technician (on orders 30+ days)")
    if (
      ![
        FAMILY_STATUS_MAP.activated.id, // id 1: "On military orders (30+ days)"
        FAMILY_STATUS_MAP.dualStatusTechnician.id, // id 7: "Dual status technician (on orders 30+ days)"
      ].includes(selectedStatusFormValue)
    ) {
      return false;
    }

    return true;
  }, [
    application?.applicant?.militaryDetails?.branch?.id,
    application?.applicant?.militaryDetails?.militaryComponent?.id,
    selectedStatusFormValue,
  ]);

  const onSubmit = useCallback(
    async (values) => {
      const { orderDatesUnknown, orderStartDate, orderEndDate, orderTitleUnknown } = values;

      const _goToNextStep = (_newApplication) => {
        // Use getNextStep to determine the next step
        const nextStep = getNextStep(FAMILY_APPLICATION_STEPS.STATUS, _newApplication, stepConfig);
        setStep({
          applicationId: _newApplication?.id,
          newStep: nextStep,
        });
      };

      if (readOnly) {
        _goToNextStep(application);
        return;
      }

      try {
        setSubmitting(true);

        // FAMILY-APPS__DATA_CLEANING - Only apply cleaning if militaryStatus actually changed
        const currentStatusId = application?.applicant?.militaryDetails?.militaryStatus?.id;
        const statusChanged = currentStatusId !== selectedStatusFormValue;

        let updatedMilitaryDetails = statusChanged
          ? applyMilitaryFieldCleaning(
              'militaryStatus',
              selectedStatusFormValue,
              application?.applicant?.militaryDetails,
            )
          : { ...application?.applicant?.militaryDetails };

        // Set the other fields that are part of this form step
        updatedMilitaryDetails = {
          ...updatedMilitaryDetails,
          orderDatesUnknown: orderDatesUnknown,
          orderStartDate: orderStartDate,
          orderEndDate: orderEndDate,
          // Secondary questions
          orderTitle: values?.orderTitle ? { id: values.orderTitle } : null,
          orderTitleUnknown: orderTitleUnknown,
          orderType: values?.orderType ? { id: values.orderType } : null,
          recruiter: values?.recruiter,
        };

        const _newSponsorData = await familyService.addUpdateAdult(household?.id, application?.applicant?.id ?? null, {
          ...application?.applicant,
          militaryDetails: updatedMilitaryDetails,
        });

        const _newApplication = await familyService.saveApplication({
          ...application,
          applicant: {
            ...application?.applicant,
            ..._newSponsorData,
          },
        });

        setApplication(_newApplication);
        _goToNextStep(_newApplication);
      } catch (error) {
        message.error('Something went wrong');
        newrelic.noticeError(error);
      } finally {
        setSubmitting(false);
      }
    },
    [application, household?.id, readOnly, selectedStatusFormValue, setApplication, setStep, stepConfig],
  );

  // Set the selected option based on the application data
  useEffect(() => {
    if (application?.applicant?.militaryDetails) {
      form.setFieldsValue({
        orderDatesUnknown: application?.applicant?.militaryDetails?.orderDatesUnknown,
        orderStartDate: application?.applicant?.militaryDetails?.orderStartDate,
        orderEndDate: application?.applicant?.militaryDetails?.orderEndDate,
        // Secondary questions
        orderType: application?.applicant?.militaryDetails?.orderType?.id,
        recruiter: application?.applicant?.militaryDetails?.recruiter,
        orderTitle: application?.applicant?.militaryDetails?.orderTitle?.id,
        orderTitleUnknown: application?.applicant?.militaryDetails?.orderTitleUnknown,
        selectedStatus: application?.applicant?.militaryDetails?.militaryStatus?.id,
      });
    }
  }, [application?.applicant?.militaryDetails, form]);

  if (loading) {
    return (
      <div className="p-8">
        <Spin className="table m-auto" />
      </div>
    );
  }

  return (
    <Spin spinning={statusOptions === null}>
      <PageMeta title="Status" />

      <Form form={form} layout="vertical" onFinish={onSubmit} disabled={readOnly}>
        <div className="p-7 pt-0">
          <Banner
            question={
              isCompletedBySponsor
                ? "What's your current status?"
                : `What's ${getApplicantFirstName(application?.applicant)}'s current work situation?`
            }
            className="my-12"
          />

          <Form.Item
            name="selectedStatus"
            rules={[
              {
                required: true,
                message: 'Please select a status',
              },
            ]}
          >
            <RadioCards
              options={statusOptions
                ?.sort((a, b) => {
                  // have isNonDeployed option
                  // all the way at the bottom of the list

                  if (a.id === NON_DEPLOYED_OPTION_ID) {
                    return 1;
                  }

                  if (b.id === NON_DEPLOYED_OPTION_ID) {
                    return -1;
                  }

                  return 0;
                })
                ?.map((_option) => {
                  const isNonDeployed = _option.id === NON_DEPLOYED_OPTION_ID;
                  const isCivilianTechnician = _option.id === CIVILIAN_TECHNICIAN_OPTION_ID;
                  const modifiedTitle =
                    application?.applicant?.militaryDetails?.branch?.id === FAMILY_BRANCH_MAP.army.id && isNonDeployed
                      ? 'Neither'
                      : isCivilianTechnician && !isCompletedBySponsor
                      ? 'Works at base - currently civilian'
                      : _option.title;

                  return {
                    label: modifiedTitle,
                    description: isCompletedBySponsor ? undefined : _option.description,
                    value: _option.id,
                  };
                })}
              onChange={(value) => {
                form.setFieldsValue({
                  orderType: undefined,
                  recruiter: undefined,
                  orderTitle: undefined,
                  orderTitleUnknown: undefined,
                  indefiniteOrders: undefined,
                  orderStartDate: undefined,
                  orderEndDate: undefined,
                  selectedStatus: value,
                });
              }}
              className="mt-8"
              data-testid="main-status-selection"
            />
          </Form.Item>

          {showOrderTitleQuestions && (
            <>
              {orderTitlesLoading ? (
                <div className="flex justify-center py-8">
                  <Spin />
                </div>
              ) : (
                orderTitles && (
                  <OrderTitleQuestions
                    applicant={application?.applicant}
                    orderTitles={orderTitles}
                    isCompletedBySponsor={isCompletedBySponsor}
                    militaryComponent={application?.applicant?.militaryDetails?.militaryComponent?.id}
                  />
                )
              )}
            </>
          )}
        </div>

        <ApplicationWizardActions
          applicationIsReadOnly={readOnly}
          backButtonProps={{
            disabled: submitting || orderTitlesLoading,
            onClick: () => {
              // Use getPreviousStep to determine the previous step
              const previousStep = getPreviousStep(FAMILY_APPLICATION_STEPS.STATUS, application, stepConfig);

              setStep({
                applicationId: application?.id,
                newStep: previousStep,
              });
            },
          }}
          nextButtonProps={{
            loading: submitting,
            disabled: orderTitlesLoading,
          }}
        />
      </Form>
    </Spin>
  );
}
